import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TradingTerminal from "../components/TradingTerminal";
import { api, getLoanCap, INVESTMENT_TIER_META, TIER_META } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, TrendingUp, ArrowDownToLine, Heart, Cpu, FileText, BadgeCheck, AlertCircle } from "lucide-react";
import { Progress } from "../components/ui/progress";

const DASH_BG = "https://images.unsplash.com/photo-1542223616-0b3b4f4a3f54?auto=format&fit=crop&w=1600&q=80"; // financial charts

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [investments, setInvestments] = useState([]);
  const [loans, setLoans] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("loan") === "1" ? "loan" : "overview");
  const [wAmount, setWAmount] = useState("");
  const [wWallet, setWWallet] = useState("");
  const [wCrypto, setWCrypto] = useState("BTC");

  const loanEnabled = user?.ai_bot_active;
  const botPending = Boolean(user?.ai_bot_pending && !user?.ai_bot_active);
  const canRequestBot = !loanEnabled && !botPending;
  const activeBot = user?.ai_bot_tier ? TIER_META[user.ai_bot_tier] : null;
  const loanCap = getLoanCap(user?.ai_bot_tier);

  const [loanForm, setLoanForm] = useState({ full_name: user?.full_name || "", amount: 5000, tier: "growth", employment_status: "Employed", country: user?.country || "" });

  const reload = async () => {
    try {
      setLoading(true);
      const [i, l, w, t] = await Promise.all([
        api.get("/investments"), api.get("/loans"), api.get("/withdrawals"), api.get("/trades")
      ]);
      setInvestments(i.data); setLoans(l.data); setWithdrawals(w.data); setTrades(t.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => { reload(); const id = setInterval(() => refreshUser(), 20000); return () => clearInterval(id); }, []); // eslint-disable-line

  useEffect(() => {
    if (!user) return;
    setLoanForm((prev) => ({
      ...prev,
      full_name: prev.full_name || user.full_name || "",
      country: prev.country || user.country || "",
      amount: loanCap ? Math.min(Math.max(Number(prev.amount) || 1, 1), loanCap) : prev.amount,
      tier: INVESTMENT_TIER_META[prev.tier] ? prev.tier : "growth",
    }));
  }, [user, loanCap]);

  const goToTab = (k) => {
    setTab(k);
    document.getElementById("dashboard-tab-content")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitLoan = async (e) => {
    e.preventDefault();
    try {
      await api.post("/loans", loanForm);
      toast.success("Loan application submitted", { description: "Under review. Approved funds will be reserved as investment credit." });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const requestWithdrawal = async (e) => {
    e.preventDefault();
    try {
      await api.post("/withdrawals", { amount: Number(wAmount), wallet_address: wWallet, crypto: wCrypto });
      toast.success("Withdrawal requested", { description: "Processing 24-48 hours" });
      setWAmount(""); setWWallet("");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handlePrimaryAction = () => {
    if (loanEnabled) {
      goToTab("loan");
    } else if (canRequestBot) {
      nav("/invest");
    }
  };

  if (!user) return null;

  const totalInvested = investments.reduce((a, b) => a + (b.amount || 0), 0);
  const activeCount = investments.filter(i => i.status === "active").length;
  const latestInvestment = investments[0];
  const pendingWithdrawals = withdrawals.filter(w => ["pending", "under_review"].includes(w.status)).length;
  const pendingLoans = loans.filter(loan => ["pending", "under_review"].includes(loan.status)).length;

  const accountInsights = [
    {
      label: "AI bot status",
      value: user?.ai_bot_active ? `${activeBot?.name || "Active"} Bot` : "Inactive",
      detail: user?.ai_bot_active ? "Unlocks real investing and loan eligibility" : "Subscribe to an AI Bot tier to unlock trading and lending",
      accent: user?.ai_bot_active ? "#00D68F" : "#D4AF37",
    },
    {
      label: "Real investment",
      value: latestInvestment ? latestInvestment.status?.replace("_", " ") || "Submitted" : "Not funded",
      detail: latestInvestment ? `${INVESTMENT_TIER_META[latestInvestment.tier]?.name || latestInvestment.tier} - $${(latestInvestment.amount || 0).toLocaleString()} trading capital` : "Fund a real investment tier after bot activation",
      accent: latestInvestment ? "#8B5CF6" : "#D4AF37",
    },
    {
      label: "Loan eligibility",
      value: user?.ai_bot_active ? `Up to $${loanCap.toLocaleString()}` : "Locked",
      detail: user?.ai_bot_active ? "Loan funds are approved as real investment credit" : "Activate a bot tier first",
      accent: user?.ai_bot_active ? "#3B82F6" : "#D4AF37",
    },
    {
      label: "Open actions",
      value: `${pendingWithdrawals + pendingLoans} pending`,
      detail: pendingWithdrawals + pendingLoans ? "Review your pending items to keep everything moving" : "Everything is current",
      accent: "#99A3BA",
    },
  ];

  const progressSteps = [
    { label: "Activate AI Bot", complete: Boolean(user?.ai_bot_active), detail: "Unlock loan access and automation" },
    { label: "Complete verification", complete: user?.kyc_status === "verified", detail: "Increase limits and speed approvals" },
    { label: "Make first investment", complete: investments.length > 0, detail: "Start earning with a live portfolio" },
  ];
  const setupProgress = Math.round((progressSteps.filter((step) => step.complete).length / progressSteps.length) * 100);

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative max-w-7xl mx-auto px-6 py-10 pb-24 overflow-hidden" data-testid="dashboard-page">
        <div className="absolute inset-0 -z-10">
          <img src={DASH_BG} alt="Financial charts" className="w-full h-full object-cover opacity-20 brightness-90" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(rgba(10,14,39,0.85), rgba(10,14,39,0.85))" }} />
        </div>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl lg:text-4xl font-black">Welcome, {user.full_name.split(" ")[0]}</h1>
              {user.account_type === "joint" && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-semibold" data-testid="joint-badge">
                  <Heart className="w-3.5 h-3.5" /> Joint Account 💑 · {user.joint_account_id}
                </span>
              )}
              {user.kyc_status === "verified" ? (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#00D68F]/15 text-[#00D68F] text-xs"><BadgeCheck className="w-3.5 h-3.5" /> Verified</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-yellow-500/15 text-yellow-300 text-xs"><AlertCircle className="w-3.5 h-3.5" /> Verification pending</span>
              )}
            </div>
            <p className="text-white/50 text-sm mt-2">{user.email} • {user.country}</p>
          </div>
          <button
            onClick={handlePrimaryAction}
            disabled={botPending}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition ${!botPending ? "btn-primary" : "bg-white/10 text-white/40 cursor-not-allowed opacity-50"}`}
            title={botPending ? "Your bot activation is pending" : user.ai_bot_active ? "Open loan application" : "Subscribe to an AI Bot tier first"}
            data-testid="activate-bot-btn"
          >
            <Cpu className="w-4 h-4" /> {botPending ? "Activation Pending" : loanEnabled ? "Apply for Loan" : "Activate AI Bot"}
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {loading ? (
            [1, 2, 3, 4].map((item) => (
              <div key={item} className="glass-strong rounded-2xl p-5 h-24 animate-pulse">
                <div className="h-3 w-20 bg-white/10 rounded mb-3" />
                <div className="h-8 w-24 bg-white/10 rounded" />
              </div>
            ))
          ) : (
            <>
              <StatCard icon={Wallet} label="Total Balance" value={`$${(user.balance || 0).toLocaleString()}`} accent="#D4AF37" testId="stat-balance" />
              <StatCard icon={TrendingUp} label="Total Returns" value={`$${(user.total_returns || 0).toLocaleString()}`} accent="#00D68F" testId="stat-returns" />
              <StatCard icon={FileText} label="Active Investments" value={`${activeCount}`} sub={`$${totalInvested.toLocaleString()} invested`} accent="#8B5CF6" testId="stat-investments" />
              <StatCard icon={ArrowDownToLine} label="Investment Credit" value={`$${(user.loan_credit || 0).toLocaleString()}`} sub={`$${(user.loan_amount || 0).toLocaleString()} total approved`} accent="#3B82F6" testId="stat-loan" />
            </>
          )}
        </div>

        <div className="glass-strong rounded-2xl p-5 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="label-eyebrow mb-2">Account insights</div>
              <h2 className="font-display text-xl font-bold">Your dashboard at a glance</h2>
              <p className="text-white/50 text-sm mt-1">Track portfolio health, pending actions, and payout timing from one place.</p>
            </div>
            <span className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00D68F]/15 text-[#00D68F] text-xs">
              <BadgeCheck className="w-3.5 h-3.5" /> 24–48 hour processing window
            </span>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
            {accountInsights.map((insight) => (
              <div key={insight.label} className="rounded-2xl border border-white/10 bg-[#04070f]/70 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-widest text-white/50">{insight.label}</p>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: insight.accent }} />
                </div>
                <div className="font-display text-2xl font-black mt-3" style={{ color: insight.accent }}>{insight.value}</div>
                <p className="text-xs text-white/50 mt-2 leading-relaxed">{insight.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-strong rounded-2xl p-5 mb-6" data-testid="user-progress-card">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <div className="label-eyebrow mb-2">User progress</div>
              <h2 className="font-display text-xl font-bold">Account setup progress</h2>
              <p className="text-white/50 text-sm mt-1">Complete the setup milestones below to unlock more activity.</p>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl font-black text-[#D4AF37]">{setupProgress}%</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">completed</div>
            </div>
          </div>
          <Progress value={setupProgress} className="bg-white/10 [&>div]:bg-gradient-to-r [&>div]:from-[#D4AF37] [&>div]:to-[#00D68F]" />
          <div className="mt-4 space-y-2">
            {progressSteps.map((step) => (
              <div key={step.label} className="flex items-center justify-between rounded-xl bg-white/5 px-3 py-2.5 text-sm">
                <div>
                  <div className="font-medium">{step.label}</div>
                  <div className="text-xs text-white/50">{step.detail}</div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${step.complete ? "bg-[#00D68F]/20 text-[#00D68F]" : "bg-yellow-500/20 text-yellow-200"}`}>
                  {step.complete ? "Done" : "Pending"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide">
          {[
            ["overview", "Overview"], ["investments", "Investments"], ["loan", "Loan"], ["withdraw", "Withdraw"]
          ].map(([k, l]) => {
            const isLoanDisabled = k === "loan" && !user.ai_bot_active;
            return (
              <button 
                key={k} 
                onClick={() => {
                  if (isLoanDisabled) return;
                  goToTab(k);
                }}
                disabled={isLoanDisabled}
                title={isLoanDisabled ? "Subscribe to an AI Bot tier first" : ""}
                className={`px-5 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  isLoanDisabled 
                    ? "bg-white/10 text-white/40 cursor-not-allowed opacity-50"
                    : tab === k 
                      ? "bg-[#D4AF37] text-[#0A0E27] font-bold"
                      : "glass text-white/70 hover:text-white"
                }`} 
                data-testid={`tab-${k}`}
              >
                {l}
              </button>
            );
          })}
        </div>

        <div id="dashboard-tab-content" />

        {tab === "overview" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <TradingTerminal active={Boolean(user.ai_bot_active)} extraTrades={trades.map(t => ({ id: t.id, pair: t.pair, side: t.side, price: t.price, profit: t.profit }))} />
            </div>
            <div className="glass-strong rounded-2xl p-5 h-fit">
              <div className="label-eyebrow mb-4">Recent Activity</div>
              <div className="space-y-3 text-sm">
                {loading ? (
                  [1, 2, 3].map((item) => (
                    <div key={item} className="flex items-center justify-between py-3 border-b border-white/5 animate-pulse">
                      <div className="space-y-2">
                        <div className="h-3 w-24 bg-white/10 rounded" />
                        <div className="h-2.5 w-16 bg-white/10 rounded" />
                      </div>
                      <div className="h-6 w-16 bg-white/10 rounded-full" />
                    </div>
                  ))
                ) : (
                  <>
                    {investments.slice(0, 5).map(inv => (
                      <div key={inv.id} className="flex items-center justify-between py-2 border-b border-white/5">
                        <div>
                          <div className="font-medium capitalize">{INVESTMENT_TIER_META[inv.tier]?.name || inv.tier} real investment</div>
                          <div className="text-xs text-white/50">${inv.amount?.toLocaleString()}</div>
                        </div>
                        <StatusBadge status={inv.status} />
                      </div>
                    ))}
                    {investments.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-white/10 p-4 text-sm text-white/60 bg-white/5">
                        No real investments yet. <a href="/invest" className="text-[#D4AF37] font-semibold">Start now</a>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "investments" && (
          <div className="glass-strong rounded-2xl p-5" data-testid="investments-tab">
            <div className="grid gap-3">
              {loading ? (
                [1, 2, 3].map((item) => (
                  <div key={item} className="grid grid-cols-2 md:grid-cols-5 gap-2 px-4 py-4 rounded-lg bg-white/5 items-center animate-pulse">
                    <div className="space-y-2">
                      <div className="h-3 w-20 bg-white/10 rounded" />
                      <div className="h-2.5 w-16 bg-white/10 rounded" />
                    </div>
                    {[1, 2, 3, 4].map((sub) => <div key={sub} className="h-3 w-12 bg-white/10 rounded" />)}
                  </div>
                ))
              ) : (
                <>
                  {investments.map(inv => (
                    <div key={inv.id} className="grid grid-cols-2 md:grid-cols-5 gap-2 px-4 py-4 rounded-lg bg-white/5 items-center">
                      <div className="capitalize">
                        <div className="font-semibold">{INVESTMENT_TIER_META[inv.tier]?.name || inv.tier}</div>
                        <div className="text-xs text-white/50">{inv.roi_percent || INVESTMENT_TIER_META[inv.tier]?.roi}% ROI</div>
                      </div>
                      <div><div className="text-xs text-white/50">Amount</div><div className="font-mono">${inv.amount?.toLocaleString()}</div></div>
                      <div><div className="text-xs text-white/50">Returns</div><div className="font-mono text-[#00D68F]">${(inv.returns || 0).toLocaleString()}</div></div>
                      <div><div className="text-xs text-white/50">Crypto</div><div>{inv.crypto}</div></div>
                      <div><StatusBadge status={inv.status} /></div>
                    </div>
                  ))}
                  {investments.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-white/60 bg-white/5">
                      <p className="font-semibold text-white">Start building your portfolio</p>
                      <p className="mt-2 text-sm">Activate your bot, then fund a real investment to see it here.</p>
                      <a href="/invest" className="mt-4 inline-flex rounded-full bg-[#D4AF37] px-4 py-2 text-sm font-bold text-[#0A0E27]">Explore plans</a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {tab === "loan" && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="loan-tab">
            {!user.ai_bot_active ? (
              <div className="glass-strong rounded-2xl p-6 lg:col-span-2">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
                    <Cpu className="w-8 h-8 text-yellow-400" />
                  </div>
                  <h2 className="font-display text-2xl font-bold mb-2">AI Bot Required</h2>
                  <p className="text-white/60 mb-6 max-w-md">
                    {botPending
                      ? "Your AI Bot activation is pending. Loan access unlocks once your subscription is active."
                      : "You must activate an AI Bot tier before you can apply for a loan. Click the button below to choose a tier."
                    }
                  </p>
                  <button
                    onClick={() => { if (canRequestBot) nav("/invest"); }}
                    disabled={!canRequestBot}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition ${canRequestBot ? "btn-primary" : "bg-white/10 text-white/40 cursor-not-allowed opacity-50"}`}
                  >
                    <Cpu className="w-4 h-4" /> {botPending ? "Waiting for Activation" : "Activate AI Bot"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <form onSubmit={submitLoan} className="glass-strong rounded-2xl p-6 space-y-4">
                  <div>
                    <div className="label-eyebrow">In-App Loan</div>
                    <h2 className="font-display text-2xl font-bold mt-1">Apply for funding</h2>
                    <p className="text-sm text-white/60 mt-2">Your {activeBot?.name || "active"} bot tier qualifies you for up to ${loanCap.toLocaleString()} in investment-only credit.</p>
                  </div>
                  <Input label="Full Name" value={loanForm.full_name} onChange={e => setLoanForm({ ...loanForm, full_name: e.target.value })} testId="loan-name" />
                  <Input label="Amount (USD)" type="number" min={1} max={loanCap} value={loanForm.amount} onChange={e => setLoanForm({ ...loanForm, amount: Number(e.target.value) })} testId="loan-amount" />
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Real investment tier</label>
                    <select value={loanForm.tier} onChange={e => setLoanForm({ ...loanForm, tier: e.target.value })} className="w-full px-4 py-3 rounded-lg" data-testid="loan-tier">
                      {Object.entries(INVESTMENT_TIER_META).map(([k, v]) => <option key={k} value={k}>{v.name} - ${v.amount.toLocaleString()}</option>)}
                    </select>
                  </div>
                  <Input label="Employment Status" value={loanForm.employment_status} onChange={e => setLoanForm({ ...loanForm, employment_status: e.target.value })} testId="loan-emp" />
                  <Input label="Country" value={loanForm.country} onChange={e => setLoanForm({ ...loanForm, country: e.target.value })} testId="loan-country" />
                  <button type="submit" className="btn-primary w-full" data-testid="loan-submit">Submit Application</button>
                </form>
                <div className="glass-strong rounded-2xl p-6">
                  <div className="label-eyebrow mb-4">Your Loan Applications</div>
                  <div className="space-y-3">
                    {loading ? (
                      [1, 2].map((item) => (
                        <div key={item} className="flex items-center justify-between p-3 rounded-lg bg-white/5 animate-pulse">
                          <div className="space-y-2">
                            <div className="h-3 w-24 bg-white/10 rounded" />
                            <div className="h-2.5 w-20 bg-white/10 rounded" />
                          </div>
                          <div className="h-6 w-16 bg-white/10 rounded-full" />
                        </div>
                      ))
                    ) : (
                      <>
                        {loans.map(l => (
                          <div key={l.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                            <div>
                              <div className="font-semibold">${l.amount?.toLocaleString()}</div>
                              <div className="text-xs text-white/50 capitalize">{INVESTMENT_TIER_META[l.tier]?.name || l.tier} real investment - {l.employment_status}</div>
                            </div>
                            <StatusBadge status={l.status} />
                          </div>
                        ))}
                        {loans.length === 0 && (
                          <div className="rounded-2xl border border-dashed border-white/10 p-4 text-sm text-white/60 bg-white/5">
                            No loan applications yet. Submit a request and it will appear here with its review status.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "withdraw" && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="withdraw-tab">
            <form onSubmit={requestWithdrawal} className="glass-strong rounded-2xl p-6 space-y-4">
              <div>
                <div className="label-eyebrow">Withdraw</div>
                <h2 className="font-display text-2xl font-bold mt-1">Cash out your returns</h2>
                <p className="text-sm text-white/60 mt-2">Available balance: <span className="text-[#D4AF37] font-bold">${(user.balance || 0).toLocaleString()}</span></p>
              </div>
              <Input label="Amount (USD)" type="number" value={wAmount} onChange={e => setWAmount(e.target.value)} testId="w-amount" />
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Crypto</label>
                <select value={wCrypto} onChange={e => setWCrypto(e.target.value)} className="w-full px-4 py-3 rounded-lg" data-testid="w-crypto">
                  <option value="BTC">Bitcoin (BTC)</option>
                  <option value="ETH">Ethereum (ETH)</option>
                  <option value="USDT">USDT (TRC-20)</option>
                </select>
              </div>
              <Input label="Your Wallet Address" value={wWallet} onChange={e => setWWallet(e.target.value)} testId="w-wallet" />
              <button type="submit" className="btn-primary w-full" data-testid="w-submit">Request Withdrawal</button>
            </form>
            <div className="glass-strong rounded-2xl p-6">
              <div className="label-eyebrow mb-4">Withdrawal History</div>
              <div className="space-y-3">
                {loading ? (
                  [1, 2, 3].map((item) => (
                    <div key={item} className="flex items-center justify-between p-3 rounded-lg bg-white/5 animate-pulse">
                      <div className="space-y-2">
                        <div className="h-3 w-24 bg-white/10 rounded" />
                        <div className="h-2.5 w-20 bg-white/10 rounded" />
                      </div>
                      <div className="h-6 w-16 bg-white/10 rounded-full" />
                    </div>
                  ))
                ) : (
                  <>
                    {withdrawals.map(w => (
                      <div key={w.id} className="p-3 rounded-lg bg-white/5">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="font-semibold">${w.amount?.toLocaleString()} {w.crypto}</div>
                            <div className="text-xs text-white/50 break-all">{w.wallet_address}</div>
                          </div>
                          <StatusBadge status={w.status} />
                        </div>
                        <div className="mt-2 text-[11px] uppercase tracking-widest text-[#D4AF37]">
                          {w.status === "pending" || w.status === "under_review" ? "Expected within 24–48 hours" : w.status === "completed" ? "Completed" : "Approved and processing"}
                        </div>
                      </div>
                    ))}
                    {withdrawals.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-white/10 p-4 text-sm text-white/60 bg-white/5">
                        No withdrawals yet. Submit one from this panel and it will appear with its processing status.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
      <div className="fixed inset-x-4 bottom-4 z-50 md:hidden">
        <div className="glass-strong rounded-2xl border border-white/10 p-3 shadow-2xl">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => goToTab("withdraw")} className="rounded-xl bg-[#D4AF37] px-3 py-2 text-xs font-bold text-[#0A0E27]">Withdraw</button>
            <button
              onClick={handlePrimaryAction}
              disabled={botPending}
              className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${!botPending ? "bg-white/10 text-white" : "bg-white/10 text-white/40 cursor-not-allowed opacity-50"}`}
            >
              {botPending ? "Waiting Activation" : loanEnabled ? "Apply for Loan" : "Activate AI Bot"}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div className="glass-strong rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="font-display text-2xl lg:text-3xl font-black mt-2" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-yellow-500/15 text-yellow-300",
    under_review: "bg-yellow-500/15 text-yellow-300",
    active: "bg-[#00D68F]/15 text-[#00D68F]",
    completed: "bg-[#D4AF37]/15 text-[#D4AF37]",
    approved: "bg-[#00D68F]/15 text-[#00D68F]",
    rejected: "bg-red-500/15 text-red-300",
  };
  return <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${map[status] || "bg-white/10 text-white/60"}`}>{status?.replace("_", " ")}</span>;
}

function Input({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60 block mb-2">{label}</span>
      <input {...props} className="w-full px-4 py-3 rounded-lg" data-testid={testId} />
    </label>
  );
}